import { useState, useEffect } from 'react';
import { Crown } from '@phosphor-icons/react';

export const CheckersBoard = ({ state, mySymbol, currentTurn, onMove, gameOver }) => {
  const [selected, setSelected] = useState(null);
  const [validMoves, setValidMoves] = useState([]);
  const isMyTurn = currentTurn === mySymbol && !gameOver;

  useEffect(() => {
    setSelected(null);
    setValidMoves([]);
  }, [state]);

  const handleSquareClick = (row, col) => {
    if (!isMyTurn) return;
    const piece = state.board[row][col];

    // Selecting your own piece
    if (piece && piece.color === mySymbol) {
      // If forced multi-jump, only that piece can be selected
      if (state.must_continue_jump) {
        if (state.must_continue_jump.row !== row || state.must_continue_jump.col !== col) {
          return;
        }
      }
      setSelected([row, col]);
      // Compute valid moves client-side (server will validate)
      setValidMoves(computeValidMoves(state, row, col, mySymbol));
      return;
    }

    // Moving selected piece
    if (selected) {
      const [fr, fc] = selected;
      const isValidTarget = validMoves.some(([r, c]) => r === row && c === col);
      if (isValidTarget) {
        onMove([fr, fc], [row, col]);
        setSelected(null);
        setValidMoves([]);
      }
    }
  };

  const isSelected = (row, col) => selected && selected[0] === row && selected[1] === col;
  const isValidTarget = (row, col) => validMoves.some(([r, c]) => r === row && c === col);

  // Rotate board so player's pieces are always at bottom
  const displayRows = mySymbol === 'red' ? [...Array(8).keys()] : [...Array(8).keys()].reverse();
  const displayCols = mySymbol === 'red' ? [...Array(8).keys()] : [...Array(8).keys()].reverse();

  return (
    <div className="flex items-center justify-center p-8" data-testid="checkers-board">
      <div>
        <div className="text-center mb-4">
          <p className="text-sm text-[#71717A]">
            You are: <span className={`font-mono font-bold ${mySymbol === 'red' ? 'text-[#EF4444]' : 'text-[#09090B]'}`}>
              {mySymbol.toUpperCase()}
            </span>
          </p>
          {!gameOver && (
            <p className="text-sm font-medium mt-1">
              {isMyTurn ? (
                <span className="text-[#10B981]">Your turn</span>
              ) : (
                <span className="text-[#71717A]">Opponent&apos;s turn ({currentTurn})</span>
              )}
            </p>
          )}
        </div>
        
        <div className="inline-block bg-[#09090B] p-2">
          {displayRows.map((row) => (
            <div key={row} className="flex">
              {displayCols.map((col) => {
                const piece = state.board[row][col];
                const isDark = (row + col) % 2 === 1;
                const selectedHere = isSelected(row, col);
                const validHere = isValidTarget(row, col);
                
                return (
                  <button
                    key={`${row}-${col}`}
                    onClick={() => handleSquareClick(row, col)}
                    disabled={!isMyTurn}
                    className={`w-12 h-12 flex items-center justify-center relative transition-all ${
                      isDark ? 'bg-[#8B4513]' : 'bg-[#F5DEB3]'
                    } ${selectedHere ? 'ring-4 ring-yellow-400 ring-inset' : ''} ${
                      validHere ? 'ring-4 ring-[#10B981] ring-inset' : ''
                    }`}
                    data-testid={`checkers-square-${row}-${col}`}
                  >
                    {piece && (
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center shadow-md ${
                        piece.color === 'red' 
                          ? 'bg-gradient-to-br from-[#EF4444] to-[#B91C1C]' 
                          : 'bg-gradient-to-br from-[#374151] to-[#111827]'
                      }`}>
                        {piece.king && <Crown size={16} weight="fill" className="text-yellow-300" />}
                      </div>
                    )}
                    {validHere && !piece && (
                      <div className="w-3 h-3 rounded-full bg-[#10B981] opacity-60"></div>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        {state.must_continue_jump && (
          <p className="text-center mt-3 text-sm text-[#EF4444] font-medium">
            You must continue jumping!
          </p>
        )}
      </div>
    </div>
  );
};

// Client-side move computation (for showing valid moves, server validates)
function computeValidMoves(state, row, col, color) {
  const piece = state.board[row][col];
  if (!piece || piece.color !== color) return [];

  const dirs = piece.king
    ? [[-1, -1], [-1, 1], [1, -1], [1, 1]]
    : piece.color === 'red'
      ? [[-1, -1], [-1, 1]]
      : [[1, -1], [1, 1]];

  const jumps = [];
  for (const [dr, dc] of dirs) {
    const mr = row + dr, mc = col + dc;
    const er = row + 2*dr, ec = col + 2*dc;
    if (er < 0 || er >= 8 || ec < 0 || ec >= 8) continue;
    if (state.board[er][ec] !== null) continue;
    const midPiece = state.board[mr][mc];
    if (!midPiece || midPiece.color === color) continue;
    jumps.push([er, ec]);
  }

  // If any jump exists for this color, only jumps are valid
  const hasAnyJump = hasJumpForColor(state.board, color);
  if (hasAnyJump || state.must_continue_jump) {
    return jumps;
  }

  const moves = [];
  for (const [dr, dc] of dirs) {
    const er = row + dr, ec = col + dc;
    if (er < 0 || er >= 8 || ec < 0 || ec >= 8) continue;
    if (state.board[er][ec] === null) moves.push([er, ec]);
  }
  return moves;
}

function hasJumpForColor(board, color) {
  for (let r = 0; r < 8; r++) {
    for (let c = 0; c < 8; c++) {
      const p = board[r][c];
      if (!p || p.color !== color) continue;
      const dirs = p.king
        ? [[-1, -1], [-1, 1], [1, -1], [1, 1]]
        : color === 'red'
          ? [[-1, -1], [-1, 1]]
          : [[1, -1], [1, 1]];
      for (const [dr, dc] of dirs) {
        const mr = r + dr, mc = c + dc;
        const er = r + 2*dr, ec = c + 2*dc;
        if (er < 0 || er >= 8 || ec < 0 || ec >= 8) continue;
        if (board[er][ec] !== null) continue;
        const midPiece = board[mr][mc];
        if (midPiece && midPiece.color !== color) return true;
      }
    }
  }
  return false;
}
